/**
 * Theme manager — gère light/dark mode via CSS custom properties.
 *
 * Règles d'accès (centralisées via canUseTheme) :
 *   - Par défaut, le thème est forcé en light pour tous les utilisateurs.
 *   - Seuls les administrateurs peuvent customiser (dark / auto).
 *   - Même si localStorage contient 'dark', un non-admin reste en light :
 *     la préférence est conservée (pas effacée) pour le cas où le user
 *     deviendrait admin plus tard.
 *
 * Usage:
 *   import { initTheme, applyThemeForUser, setTheme, canUseTheme } from 'libs/theme';
 *
 *   initTheme();                          // au boot : applique light par défaut
 *   applyThemeForUser(currentUser);       // une fois le user chargé : applique selon permission
 *   setTheme('dark', currentUser);        // change le thème (check perm)
 *   canUseTheme(currentUser);             // booléen : user autorisé à customiser ?
 */

import USER_ROLES from '../const/userRoles';

const STORAGE_KEY = 'voissa-theme';
const THEME_LIGHT = 'light';
const THEME_DARK = 'dark';
const THEME_AUTO = 'auto';

/**
 * Détermine si un utilisateur a le droit de customiser le thème.
 * Point unique pour ajouter d'autres conditions (rôles, features flags, etc.).
 */
export function canUseTheme(user) {
    return user?.role === USER_ROLES.ADMINISTRATOR;
}

function getSystemPreference() {
    if (typeof window === 'undefined' || !window.matchMedia) return THEME_LIGHT;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME_DARK : THEME_LIGHT;
}

function getStoredPreference() {
    try {
        return localStorage.getItem(STORAGE_KEY) || THEME_AUTO;
    } catch (e) {
        return THEME_AUTO;
    }
}

function applyTheme(mode) {
    if (typeof document === 'undefined') return;
    const resolved = mode === THEME_AUTO ? getSystemPreference() : mode;
    document.documentElement.setAttribute('data-theme', resolved);
}

/**
 * Init au boot : applique light par défaut sans toucher au localStorage.
 * Le user n'étant pas encore chargé, on ne peut pas vérifier sa permission.
 * applyThemeForUser() prendra le relais une fois le user résolu (cf. useTheme).
 */
export function initTheme() {
    applyTheme(THEME_LIGHT);

    // Écoute des changements de pref système — actif seulement si data-theme
    // n'est pas forcé light (ie. user admin en mode auto).
    if (typeof window !== 'undefined' && window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            if (getStoredPreference() === THEME_AUTO
                && document.documentElement.dataset.themeAccess === 'granted') {
                applyTheme(THEME_AUTO);
            }
        };
        if (mq.addEventListener) {
            mq.addEventListener('change', handler);
        } else if (mq.addListener) {
            mq.addListener(handler);
        }
    }
}

/**
 * Applique le thème adapté au user : sa préférence stockée s'il est autorisé,
 * sinon force light. Ne touche pas au localStorage (préserve la pref).
 */
export function applyThemeForUser(user) {
    if (!canUseTheme(user)) {
        // Flag pour désactiver la sync système (cf. handler dans initTheme).
        if (typeof document !== 'undefined') delete document.documentElement.dataset.themeAccess;
        applyTheme(THEME_LIGHT);
        return;
    }
    if (typeof document !== 'undefined') document.documentElement.dataset.themeAccess = 'granted';
    applyTheme(getStoredPreference());
}

/**
 * Modifie le thème courant. Refusé si user non autorisé.
 * Retourne true si appliqué, false sinon.
 */
export function setTheme(mode, user) {
    if (!canUseTheme(user)) {
        console.warn('User not authorized to change theme');
        return false;
    }
    if (![THEME_LIGHT, THEME_DARK, THEME_AUTO].includes(mode)) {
        console.warn(`Invalid theme mode: ${mode}`);
        return false;
    }
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
        // localStorage might be unavailable
    }
    applyTheme(mode);
    return true;
}

export function toggleTheme(user) {
    const current = getTheme();
    return setTheme(current === THEME_DARK ? THEME_LIGHT : THEME_DARK, user);
}

export function getTheme() {
    if (typeof document === 'undefined') return THEME_LIGHT;
    return document.documentElement.getAttribute('data-theme') || THEME_LIGHT;
}

export function getStoredTheme() {
    return getStoredPreference();
}

export const THEMES = {
    LIGHT: THEME_LIGHT,
    DARK: THEME_DARK,
    AUTO: THEME_AUTO,
};
